import { isValidPhoneHelper } from '@elfsight/lib-utils';
import {
  AsYouType,
  getCountryCallingCode,
  Metadata,
  validatePhoneNumberLength
} from 'libphonenumber-js/max';
import type { CountryCode } from 'libphonenumber-js/types';

type MetadataWithNumberTypes = Metadata & {
  type(
    kind: 'FIXED_LINE' | 'MOBILE'
  ): { possibleLengths(): number[] | undefined } | undefined;
};

const phoneNumberMetadata = new Metadata() as MetadataWithNumberTypes;
const SUBSCRIBER_TYPES = ['FIXED_LINE', 'MOBILE'] as const;

export function getMaxNationalNumberDigits(countryCode: CountryCode) {
  try {
    phoneNumberMetadata.selectNumberingPlan(countryCode);
    const mergedPossibleNationalLengths = new Set<number>();

    for (const subscriberNumberType of SUBSCRIBER_TYPES) {
      const numberingPlanType = phoneNumberMetadata.type(subscriberNumberType);
      if (!numberingPlanType) {
        continue;
      }
      const possibleLengths = numberingPlanType.possibleLengths();
      if (!possibleLengths?.length) {
        continue;
      }
      for (const possibleNationalLength of possibleLengths) {
        mergedPossibleNationalLengths.add(possibleNationalLength);
      }
    }

    if (mergedPossibleNationalLengths.size === 0) {
      return undefined;
    }

    return Math.max(...mergedPossibleNationalLengths);
  } catch {
    return undefined;
  }
}

export function clampInternationalPhoneToMaxNationalDigits(
  rawInput: string,
  countryCode: CountryCode
) {
  const maxNationalDigits = getMaxNationalNumberDigits(countryCode);
  if (maxNationalDigits === undefined) {
    return rawInput;
  }

  const callingCode = getCountryCallingCode(countryCode);
  const digitsOnly = rawInput.replace(/\D/g, '');

  const hasNationalDigitsAfterCountryCallingCode =
    digitsOnly.length > callingCode.length &&
    digitsOnly.startsWith(callingCode);
  if (!hasNationalDigitsAfterCountryCallingCode) {
    return rawInput;
  }

  const nationalDigits = digitsOnly.slice(callingCode.length);
  if (nationalDigits.length <= maxNationalDigits) {
    return rawInput;
  }

  const truncatedNational = nationalDigits.slice(0, maxNationalDigits);
  return `+${callingCode}${truncatedNational}`;
}

export function restoreCursor(
  inputElement: HTMLInputElement | HTMLTextAreaElement | undefined | null,
  position: number
) {
  if (!inputElement) {
    return;
  }

  requestAnimationFrame(() => {
    try {
      if (document.activeElement !== inputElement) {
        inputElement.focus({ preventScroll: true });
      }
    } catch {
      if (document.activeElement !== inputElement) {
        inputElement.focus();
      }
    }
    const clampedPosition = Math.max(
      0,
      Math.min(position, inputElement.value.length)
    );
    inputElement.setSelectionRange(clampedPosition, clampedPosition);
  });
}

export function isEmptyOrRemovedPlusPrefix(
  currentInput: string,
  previousInput: string
) {
  const removedPlus =
    previousInput.startsWith('+') && !currentInput.startsWith('+');
  const cleared = currentInput === '' || currentInput === '+';
  return removedPlus || cleared;
}

interface GetNewCursorPositionArgs {
  rawInput: string;
  rawCursorIndex: number;
  formattedInput: string;
}

export function getNewCursorPosition({
  rawInput,
  rawCursorIndex,
  formattedInput
}: GetNewCursorPositionArgs) {
  const digitsBeforeCursor = (
    rawInput.slice(0, rawCursorIndex).match(/\d/g) || []
  ).length;
  let newPosition = 0;
  let digitsSeen = 0;
  while (
    newPosition < formattedInput.length &&
    digitsSeen < digitsBeforeCursor
  ) {
    if (/\d/.test(formattedInput[newPosition])) {
      digitsSeen++;
    }

    newPosition++;
  }

  if (digitsBeforeCursor === 0 && formattedInput.startsWith('+')) {
    const plusIndex = formattedInput.indexOf('+');
    newPosition = Math.min(formattedInput.length, plusIndex + 1);
  }

  return newPosition;
}

export function isPhonePossible(phone: string) {
  const isValid = isValidPhoneHelper(phone);
  const lengthValidationResult = validatePhoneNumberLength(phone);

  return (
    isValid ||
    lengthValidationResult === 'TOO_SHORT' ||
    lengthValidationResult === 'INVALID_LENGTH'
  );
}

export const formatPhonePrefix = (callingCode: string) => `+${callingCode}`;

export function prependPlusIfNeeded(
  currentInput: string,
  previousInput: string
): {
  currentInput: string;
  cursorOffset: number;
} {
  const isPasteNumberInEmptyInput =
    previousInput.length === 0 &&
    !currentInput.startsWith('+') &&
    currentInput.length > 0;

  if (isPasteNumberInEmptyInput) {
    return {
      currentInput: '+' + currentInput,
      cursorOffset: currentInput.length
    };
  }

  const isAttemptingToRemoveLeadingPlus =
    previousInput.startsWith('+') &&
    !currentInput.startsWith('+') &&
    currentInput.length > 0;

  const isStartingWithSingleDigit =
    currentInput.length === 1 && /^\d$/.test(currentInput[0]);

  if (isStartingWithSingleDigit || isAttemptingToRemoveLeadingPlus) {
    return { currentInput: '+' + currentInput, cursorOffset: 1 };
  }
  return { currentInput, cursorOffset: 0 };
}

export function getFormattedPhone(value: string, countryCode: CountryCode) {
  const phoneFormatter = new AsYouType(countryCode);
  return phoneFormatter.input(value);
}
